import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SCUIGeneralModal, SCUISnackbar } from '../../../../atomic/molecules';
import { Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { SCUIButton, SCUIDateRanger } from '../../../../atomic/atoms';
import { RuleServiceCommand } from '../../../../services/command';
import { Operators, RuleTenant } from '../../RuleService.const';
import { getSnackbarValue, validateRule } from '../../helper/RuleModal.helper';
import { enumSnackbarStatusType } from '../../../../types/enums';

const countryCode = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryCode?.toString();

export const SCUIRuleModal: FC<{
    open: boolean;
    tenant: any;
    onClose: any;
    ruleTemplates: any;
    currentRule: any;
}> = ({ open, tenant, onClose, ruleTemplates, currentRule }) => {
    const { t } = useTranslation();
    const [criteriaTypes, setCriteriaTypes] = useState([]);
    const [date, setDate] = useState({
        fromDate: '' as any,
        toDate: '' as any,
    });
    const [selectedAllIndex, setSelectedAllIndex] = useState([] as any[]);
    const [intervalValues, setIntervalValues] = useState([] as any[]);
    const [modalState, setModalState] = useState({
        id: 0,
        ruleTemplateId: '',
        ruleKey: '',
        regionId: JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.regionId,
        action: '',
        priority: '' as string | number,
        Tenant: tenant,
        criteria: [] as any,
    });
    const [snackbar, setSnackbar] = useState({
        show: false,
        message: '',
        status: enumSnackbarStatusType.success,
    });
    const _handleChangeDate = (event: any) => {
        setDate({
            fromDate: event[0],
            toDate: event[1],
        });
    };
    const updateOperatorValue = (value: any, criteriaTypeKey: any, operator: any, index: number, key: keyof any) => {
        let operatorValue = intervalValues;
        operatorValue[index] = { ...operatorValue[index] };
        operatorValue[index][key] = value;
        setIntervalValues(operatorValue);

        let criteriaValue = `${intervalValues[index]?.ValueA ?? ''}#${intervalValues[index]?.ValueB ?? ''}`;

        updateChildItem(criteriaValue, criteriaTypeKey, operator, index);
    };
    useEffect(() => {
        if (modalState.ruleTemplateId) {
            let criteriaTypes = ruleTemplates.find((item: any) => item.id == modalState.ruleTemplateId)?.criteriaTypes;
            setCriteriaTypes(criteriaTypes);
        }
    }, [modalState.ruleTemplateId]);
    useEffect(() => {
        if (currentRule.id) {
            setModalState({
                id: currentRule.id as number,
                action: currentRule.action,
                priority: currentRule.priority,
                ruleTemplateId: currentRule.ruleTemplateId,
                ruleKey: currentRule.ruleKey,
                criteria: currentRule.criteria,
                regionId: currentRule.regionId,
                Tenant: currentRule.tenant,
            });
            setDate({
                fromDate: new Date(currentRule.effectiveDate).toDateString(),
                toDate: new Date(currentRule.endDate).toDateString(),
            });
            currentRule?.criteria?.forEach((item: any, index: number) => {
                if (item.value.includes('*')) {
                    let items = [...selectedAllIndex];
                    items[index] = { disabled: true };
                    setSelectedAllIndex(items);
                } else {
                    let items = [...selectedAllIndex];
                    items[index] = { disabled: false };
                    setSelectedAllIndex(items);
                }

                if (
                    item.operator == Operators()[1].value ||
                    item.operator == Operators()[2].value ||
                    item.operator == Operators()[3].value ||
                    item.operator == Operators()[4].value
                ) {
                    let values = item.value.split('#');
                    let intervalValues = [];
                    intervalValues[index] = { ValueA: values[0], ValueB: values[1] };
                    setIntervalValues(intervalValues);
                }
            });
        }
    }, [currentRule]);
    const save = async (modalState: any) => {
        let currentPayload = modalState;
        if (currentPayload) {
            let snackBarSettings = {} as any;
            try {
                if (date.fromDate) {
                    currentPayload.effectiveDate = new Date(date.fromDate).toDateString();
                }
                if (date.toDate) {
                    currentPayload.endDate = new Date(date.toDate).toDateString();
                }

                let validation = validateRule(currentPayload);
                if (!validation.isValid) {
                    setSnackbar({
                        show: true,
                        message: validation.messages.join(', '),
                        status: enumSnackbarStatusType.danger,
                    });
                    return;
                }

                let response = await RuleServiceCommand.CreateRule({
                    rules: [currentPayload],
                });
                snackBarSettings = getSnackbarValue(response);
            } catch {
                snackBarSettings = getSnackbarValue(null);
            }
            onClose(snackBarSettings);
        }
    };
    const updateChildItem = (value: any, criteriaTypeKey: any, operator: number, index: number) => {
        let criteria = modalState.criteria;
        criteria[index] = {
            key: criteriaTypeKey,
            operator: operator,
            value: value,
        };
        setModalState((prevState) => ({
            ...prevState,
            criteria: criteria,
        }));
    };
    const setSelectedAll = (val: boolean, index: number) => {
        let items = [...selectedAllIndex];
        items[index] = { disabled: val };
        setSelectedAllIndex(items);
    };
    return (
        <SCUIGeneralModal
            sx={{ minWidth: '700px !important' }}
            width={'xs'}
            open={open}
            onClose={onClose}
            title={t('ruleManagement.modal.rule.title')}
            buttonText={t('general.save')}
        >
            <Grid container sx={{ padding: '2rem' }} style={{ display: 'flex' }}>
                <FormControl style={{ width: '100%', paddingBottom: '1rem' }}>
                    <InputLabel id={'ruleTemplateId'}>{t('ruleManagement.fields.rule.ruleTemplate.name')}</InputLabel>
                    <Select
                        labelId={'ruleTemplateId'}
                        id={'field.name'}
                        size="medium"
                        value={modalState.ruleTemplateId}
                        onChange={(event: any) => {
                            setModalState((prevState) => ({
                                ...prevState,
                                ruleTemplateId: event.target.value,
                            }));
                        }}
                        label={t('ruleManagement.fields.rule.ruleTemplate.name')}
                    >
                        {ruleTemplates &&
                            ruleTemplates.map((ruleTemplate: any, index: number) => (
                                <MenuItem key={index} id={ruleTemplate.id} value={ruleTemplate.id}>
                                    {ruleTemplate.name}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth>
                    <TextField
                        required={true}
                        sx={{ paddingBottom: '1rem' }}
                        value={RuleTenant[tenant as keyof typeof RuleTenant]}
                        label={'Tenant'}
                        disabled={true}
                        multiline
                        fullWidth
                    />
                </FormControl>
                <FormControl style={{ width: '100%', paddingBottom: '1rem' }}>
                    <InputLabel id={'Region'}>{t('general.region')}</InputLabel>
                    <Select
                        labelId={'Region'}
                        id={'Region'}
                        label={t('general.region')}
                        size="medium"
                        disabled={true}
                        value={countryCode}
                    >
                        <MenuItem value={countryCode}>{countryCode}</MenuItem>
                    </Select>
                </FormControl>
                {
                    <FormControl fullWidth>
                        <TextField
                            required={true}
                            sx={{ paddingBottom: '1rem' }}
                            value={modalState.ruleKey}
                            label={t('ruleManagement.fields.rule.ruleKey.name')}
                            disabled={false}
                            multiline
                            onChange={(event: any) => {
                                setModalState((prevState) => ({
                                    ...prevState,
                                    ruleKey: event.target.value,
                                }));
                            }}
                            fullWidth
                        />
                    </FormControl>
                }
                {
                    <FormControl fullWidth>
                        <TextField
                            required={true}
                            sx={{ paddingBottom: '1rem' }}
                            value={modalState.priority}
                            label={t('ruleManagement.fields.rule.priority.name')}
                            disabled={false}
                            multiline
                            onChange={(event: any) => {
                                setModalState((prevState) => ({
                                    ...prevState,
                                    priority: parseInt(event.target.value),
                                }));
                            }}
                            fullWidth
                        />
                    </FormControl>
                }
                {
                    <FormControl style={{ width: '100%', paddingBottom: '1rem' }}>
                        <SCUIDateRanger
                            fromDate={date.fromDate}
                            toDate={date.toDate}
                            setDateRange={_handleChangeDate}
                        />
                    </FormControl>
                }
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {criteriaTypes &&
                        criteriaTypes.map((item: any, index: number) => (
                            <div key={index} style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ flexDirection: 'row' }}>
                                    <TextField
                                        required={true}
                                        sx={{ paddingBottom: '1rem' }}
                                        value={item.name}
                                        label={t('ruleManagement.fields.ruleTemplate.criteriaType.name')}
                                        disabled={true}
                                        multiline
                                        fullWidth
                                    />
                                </div>
                                <div style={{ display: 'flex' }}>
                                    <FormControl fullWidth sx={{ paddingBottom: '1rem' }}>
                                        <InputLabel id={'Operator'}>{Operators()[item.operator].label}</InputLabel>
                                        <Select
                                            labelId={'Operator'}
                                            disabled={true}
                                            id={'Operator'}
                                            size="medium"
                                            label={'Operator'}
                                        >
                                            <MenuItem id={'Operator'} value={Operators()[item.operator].value}>
                                                {' '}
                                                {Operators()[item.operator].label}{' '}
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                    <div style={{ width: '100%', marginLeft: '10px' }}>
                                        {!item.hasPredefinedValue && (item.operator == 0 || item.operator == 5) && (
                                            <FormControl fullWidth>
                                                <TextField
                                                    required={true}
                                                    value={modalState.criteria[index]?.value}
                                                    onChange={(event: any) => {
                                                        updateChildItem(
                                                            event.target.value,
                                                            item.key,
                                                            Operators()[item.operator].value,
                                                            index
                                                        );
                                                    }}
                                                    label={'Value'}
                                                    multiline
                                                    fullWidth
                                                />
                                            </FormControl>
                                        )}
                                        {!item.hasPredefinedValue &&
                                            (item.operator == Operators()[1].value ||
                                                item.operator == Operators()[2].value ||
                                                item.operator == Operators()[3].value ||
                                                item.operator == Operators()[4].value) && (
                                                <FormControl
                                                    sx={{ display: 'flex', 'flex-direction': 'row' }}
                                                    fullWidth
                                                >
                                                    <TextField
                                                        required={true}
                                                        value={intervalValues[index]?.ValueA}
                                                        onKeyDown={(event) => {
                                                            if (!/[0-9]/.test(event.key)) {
                                                                event.preventDefault();
                                                            }
                                                        }}
                                                        onChange={(event: any) => {
                                                            updateOperatorValue(
                                                                event.target.value,
                                                                item.key,
                                                                Operators()[item.operator].value,
                                                                index,
                                                                'ValueA'
                                                            );
                                                        }}
                                                        label={'ValueA'}
                                                        multiline
                                                        sx={{ 'margin-right': '5px' }}
                                                        fullWidth
                                                    />
                                                    <TextField
                                                        required={true}
                                                        onKeyDown={(event) => {
                                                            if (!/[0-9]/.test(event.key)) {
                                                                event.preventDefault();
                                                            }
                                                        }}
                                                        value={intervalValues[index]?.ValueB}
                                                        onChange={(event: any) => {
                                                            updateOperatorValue(
                                                                event.target.value,
                                                                item.key,
                                                                Operators()[item.operator].value,
                                                                index,
                                                                'ValueB'
                                                            );
                                                        }}
                                                        label={'ValueB'}
                                                        multiline
                                                        fullWidth
                                                    />
                                                </FormControl>
                                            )}
                                        {item.hasPredefinedValue && item.criteriaTypeValues && (
                                            <FormControl
                                                sx={{
                                                    'flex-direction': 'row',
                                                    'justify-content': 'space-between',
                                                }}
                                                fullWidth
                                            >
                                                <InputLabel id={'criteriaTypeValue.id'}>{item.name}</InputLabel>
                                                <Select
                                                    sx={{ flex: 6 }}
                                                    labelId={'Value'}
                                                    id={'criteriaTypeValue.id'}
                                                    disabled={selectedAllIndex[index]?.disabled}
                                                    size="medium"
                                                    value={modalState.criteria[index]?.value}
                                                    onChange={(event: any) => {
                                                        updateChildItem(
                                                            event.target.value,
                                                            item.key,
                                                            Operators()[item.operator].value,
                                                            index
                                                        );
                                                    }}
                                                    label={'Value'}
                                                >
                                                    {item.criteriaTypeValues &&
                                                        item.criteriaTypeValues.map(
                                                            (criteriaTypeValue: any, index: number) => (
                                                                <MenuItem
                                                                    key={index}
                                                                    id={criteriaTypeValue.id}
                                                                    value={criteriaTypeValue.value}
                                                                >
                                                                    {criteriaTypeValue.name}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                </Select>

                                                <FormControlLabel
                                                    sx={{ padding: '10px' }}
                                                    control={
                                                        <Checkbox
                                                            sx={{ flex: 6 }}
                                                            title={'Hepsi'}
                                                            id={'all'}
                                                            checked={selectedAllIndex[index]?.disabled}
                                                            onChange={() => {
                                                                if (!selectedAllIndex[index]?.disabled) {
                                                                    setSelectedAll(
                                                                        !selectedAllIndex[index]?.disabled,
                                                                        index
                                                                    );
                                                                    updateChildItem(
                                                                        '',
                                                                        item.key,
                                                                        Operators()[item.operator].value,
                                                                        index
                                                                    );
                                                                } else {
                                                                    setSelectedAll(
                                                                        !selectedAllIndex[index]?.disabled,
                                                                        index
                                                                    );
                                                                    updateChildItem(
                                                                        '*',
                                                                        item.key,
                                                                        Operators()[item.operator].value,
                                                                        index
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label="Hepsi"
                                                />
                                            </FormControl>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>
                {
                    <FormControl fullWidth>
                        <TextField
                            required={true}
                            sx={{ paddingBottom: '1rem' }}
                            value={modalState.action}
                            label={t('ruleManagement.fields.rule.action.name')}
                            disabled={false}
                            multiline
                            onChange={(event: any) => {
                                setModalState((prevState) => ({
                                    ...prevState,
                                    action: event.target.value,
                                }));
                            }}
                            fullWidth
                        />
                    </FormControl>
                }
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row-reverse',
                        width: '100%',
                    }}
                >
                    <div className="m-l-16">
                        <SCUIButton
                            type="primary"
                            sizeType="fixed"
                            text={t('general.save')}
                            onClick={() => {
                                save(modalState);
                            }}
                            size="s"
                        ></SCUIButton>
                    </div>

                    <SCUIButton
                        type="secondary"
                        sizeType="fixed"
                        text={t('general.cancel')}
                        size="s"
                        onClick={onClose}
                    ></SCUIButton>
                </div>
            </Grid>
            <SCUISnackbar
                show={snackbar.show}
                messageText={snackbar.message}
                status={snackbar.status}
                autoHideDuration={2000}
                onClose={() => {
                    setSnackbar({
                        show: false,
                        message: '',
                        status: enumSnackbarStatusType.success,
                    });
                }}
            />
        </SCUIGeneralModal>
    );
};
